import React, { useEffect, useMemo, useState } from "react";
import * as styles from "./MainNavigationVer2.module.scss";
import { getCookie } from "../../utils";
import { classList } from "../../lib/helpers";
import { LogIn, Menu, UserPlus, X, LogOut } from "react-feather";
import { getBasePathForLanguage } from "../../../sanity/plugins/multilanguage";
import { LanguageSwitch } from "../LanguageSwitch/LanguageSwitch";
import { FocusTrap } from "focus-trap-react";
import { useBreakpoint } from "gatsby-plugin-breakpoints";

export interface MainNavigationProps {
  children?: React.ReactNode;
  registerCta?: string;
  loginCta?: string;
  logoutCta?: string;
  language: string;
  availableLanguages?: string[];
}

export const MainNavigationVer2: React.FC<MainNavigationProps> = ({
  children,
  registerCta,
  loginCta,
  language,
  availableLanguages,
}) => {
  const [toggled, setToggled] = useState(false);
  const [isLogin, setIsLogin] = useState<boolean>(false);
  const breakpoints = useBreakpoint();

  const isMobile = breakpoints.md; // up to 768px
  const isTablet = breakpoints.lg_2 && !breakpoints.md; // 769px - 1024px
  const isDesktop = !breakpoints.lg_2; // 1025 and up

  const classes = classList(
    styles.mainNavigation,
    toggled && styles.mainNavigation_toggled
  );

  const loginBtnLabel = useMemo(() => {
    if (isLogin) {
      if (language === "de") {
        return "Zu Casio Education";
      }
      if (language === "fr") {
        return "Aller à Casio Education";
      }

      return "Go to Casio Education";
    } else {
      return loginCta;
    }
  }, [isLogin, language, loginCta]);

  const loginBtnAriaLabel = useMemo(() => {
    if (isLogin) {
      if (language === "de") {
        return "Casio Education Webanwendung öffnen";
      }
      if (language === "fr") {
        return "Ouvrir l'application web Casio Education";
      }

      return "Open Casio Education Web Application";
    } else {
      if (language === "de") {
        return "Anmelden – Anmeldeseite öffnen";
      }
      if (language === "fr") {
        return "Connexion au compte CASIO Education";
      }
    }
  }, [isLogin, language]);

  useEffect(() => {
    const isLogin = getCookie("isLogin");
    setIsLogin(Boolean(isLogin));
  }, []);

  return (
    <nav className={classes}>
      {isDesktop && (
        <div className={styles.mainNavigation__links_desktop}>{children}</div>
      )}

      {language !== "en" && isDesktop && (
        <>
          <li
            aria-label={
              isLogin
                ? "Navigation Go To Casio Education"
                : "Navigation Login Button"
            }
            className={styles.mainNavigation__registrationCta}
          >
            <NavAction
              icon={<LogIn />}
              href={getBasePathForLanguage(language) + "app"}
              label={loginBtnLabel || ""}
              type="primary"
              ariaLabel={loginBtnAriaLabel} // WCAG-Todo
              className={styles.mainNavigation__loginBtn}
            />
          </li>

          {!isLogin && (
            <li
              className={styles.mainNavigation__registrationCta}
              aria-label="Navigation Register Button"
            >
              <NavAction
                icon={<UserPlus />}
                href={getBasePathForLanguage(language) + "app/registration"}
                label={registerCta || "Register"}
                type="secondary"
                ariaLabel={
                  language === "de"
                    ? "Jetzt registrieren – Registrierungsformular öffnen"
                    : "Créer un compte CASIO Education"
                } // WCAG-Todo
                className={styles.mainNavigation__loginBtn}
              />
            </li>
          )}
          {isLogin && (
            <li
              className={styles.mainNavigation__registrationCta}
              aria-label="Navigation Logout Button"
            >
              <NavAction
                icon={<LogOut />}
                href={"/app/log-out"}
                label={
                  getBasePathForLanguage(language) === "/fr/"
                    ? "Se déconnecter"
                    : "Abmelden"
                } // WCAG-Todo
                type="secondary"
                ariaLabel="Logout Button" // WCAG-Todo
                className={styles.mainNavigation__loginBtn}
              />
            </li>
          )}
        </>
      )}

      {availableLanguages && availableLanguages?.length >= 2 && (
        <LanguageSwitch
          currentLanguage={language}
          availableLanguages={availableLanguages}
          className={styles.mainNavigation__languageSwitch}
        />
      )}

      {(isMobile || isTablet) && !toggled && (
        <button onClick={() => setToggled(!toggled)}>
          <i>
            <Menu aria-label="Button Show Menu" />
          </i>
        </button>
      )}

      {(isMobile || isTablet) && (
        <FocusTrap active={toggled}>
          <div className={styles.mainNavigation__wrapper}>
            <div
              className={
                styles[
                  `mainNavigation__links_mobile${!toggled ? "_hidden" : ""}`
                ]
              }
            >
              {children}
            </div>

            <div
              className={
                styles[
                  `mainNavigation__wrapper__groupBtn${
                    !toggled ? "_hidden" : ""
                  }`
                ]
              }
            >
              <NavAction
                icon={<LogIn />}
                href={getBasePathForLanguage(language) + "app"}
                label={loginBtnLabel || ""}
                type="primary"
                ariaLabel={loginBtnAriaLabel} // WCAG-Todo
                className={styles.mainNavigation__loginBtnMobileTablet}
              />

              {!isLogin && (
                <div
                  className={
                    styles.mainNavigation__registrationCta_mobileTablet
                  }
                >
                  <NavAction
                    icon={<UserPlus />}
                    href={getBasePathForLanguage(language) + "app/registration"}
                    label={registerCta || "Register"}
                    type="secondary"
                    ariaLabel={
                      language === "de"
                        ? "Jetzt registrieren – Registrierungsformular öffnen"
                        : "Créer un compte CASIO Education"
                    } // WCAG-Todo
                  />
                </div>
              )}
              {isLogin && (
                <div
                  className={
                    styles.mainNavigation__registrationCta_mobileTablet
                  }
                >
                  <NavAction
                    icon={<LogOut />}
                    href={"/app/log-out"}
                    label={
                      getBasePathForLanguage(language) === "/fr/"
                        ? "Se déconnecter"
                        : "Abmelden"
                    } // WCAG-Todo
                    type="secondary"
                    ariaLabel="Logout Button" // WCAG-Todo
                  />
                </div>
              )}
            </div>

            <button
              onClick={() => setToggled(!toggled)}
              className={
                styles[
                  `mainNavigation__wrapper_${toggled ? "toggled" : "hidden"}`
                ]
              }
            >
              <i>
                <X aria-label="Button Close Menu" style={{ color: "white" }} />
              </i>
            </button>
          </div>
        </FocusTrap>
      )}
      <div
        className={styles.mainNavigation__backdrop}
        onClick={() => setToggled(!toggled)}
      ></div>
    </nav>
  );
};

export const NavAction: React.FC<{
  type?: "primary" | "secondary";
  icon?: React.ReactNode;
  href: string;
  label: string;
  className?: string;
  ariaLabel?: string;
}> = ({ type = "primary", icon, href, label, className, ariaLabel }) => {
  const classes = classList(
    styles.navAction,
    styles["navAction_" + type],
    className && className
  );
  return (
    <a href={href} className={classes} aria-label={ariaLabel}>
      <i>{icon}</i>
      <span>{label}</span>
    </a>
  );
};
